import { useMemo, useRef } from 'react';
import * as THREE from 'three';

/**
 * Stylised footballer composed of high-polysphere/capsule primitives —
 * head, torso (jersey), shorts, legs (skin + socks), boots, arms (sleeve
 * + skin), shoulders. No external GLB asset.
 *
 * Why rounded primitives? Box-geometry primitives read as Roblox-style
 * blocks; capsule geometry has hemispherical end-caps that catch the
 * environment HDRI properly and produce smooth silhouettes from every
 * camera angle. Combined with PBR materials + image-based lighting +
 * ACES tone mapping in TacticsBoard3D, the figure should read as
 * stylised-AAA rather than blocky.
 *
 * The figure stands ~1.95m tall (close to a real player, before the
 * outer FIGURE_SCALE in PlayerToken3D). Origin sits at the boots so
 * `position.y = 0` plants the figure on the grass.
 */
interface Props {
  teamHex: string;
  selected: boolean;
  /** Random phase 0..2π so a row of players don't bob in unison. */
  idlePhase: number;
  /** When true the idle bob + arm sway hold their neutral pose so the
   *  scene reads as static — honours `prefers-reduced-motion: reduce`. */
  reducedMotion?: boolean;
}

const SKIN = '#c69074';
const HAIR = '#1f1813';
const BOOT_BODY = '#0f0f0f';
const BOOT_SOLE = '#222222';

// Lighten/darken a CSS hex by a 0..1 factor toward white/black. Used to
// derive shorts (darker) + secondary kit accents from the primary kit hex
// without requiring callers to pass three colours.
function shade(hex: string, amount: number): string {
  const c = new THREE.Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  hsl.l = Math.max(0, Math.min(1, hsl.l + amount));
  return new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l).getHexString();
}

export function ProceduralFootballer({ teamHex, selected, idlePhase, reducedMotion = false }: Props) {
  const groupRef = useRef<THREE.Group>(null);

  // Derived kit palette. Shorts are darker than the jersey; socks pick up
  // the jersey hue at a slight lift for a TV-broadcast colour-block feel.
  const palette = useMemo(() => {
    const shortsHex = `#${shade(teamHex, -0.22)}`;
    const sockHex = `#${shade(teamHex, 0.05)}`;
    const sleeveTrimHex = `#${shade(teamHex, -0.12)}`;
    return { shortsHex, sockHex, sleeveTrimHex };
  }, [teamHex]);

  // Build materials once per palette — sharing across all primitive meshes
  // for this figure keeps the GPU upload count down and lets the selection
  // glow flip via a single emissive flag per material.
  // PBR roughness/metalness chosen so PBR + HDRI Environment lighting
  // gives realistic kit/skin/leather response. Skin is high roughness;
  // jersey is matte cloth (rough 0.55, no metalness); boots are
  // synthetic with a hint of metal for the studs/coating.
  const mats = useMemo(() => {
    const jersey = new THREE.MeshStandardMaterial({
      color: teamHex,
      roughness: 0.55,
      metalness: 0.0,
    });
    const sleeveTrim = new THREE.MeshStandardMaterial({
      color: palette.sleeveTrimHex,
      roughness: 0.5,
      metalness: 0.0,
    });
    const shorts = new THREE.MeshStandardMaterial({
      color: palette.shortsHex,
      roughness: 0.62,
      metalness: 0.0,
    });
    const sock = new THREE.MeshStandardMaterial({
      color: palette.sockHex,
      roughness: 0.55,
    });
    const skin = new THREE.MeshStandardMaterial({
      color: SKIN,
      roughness: 0.78,
      metalness: 0.02,
    });
    const hair = new THREE.MeshStandardMaterial({
      color: HAIR,
      roughness: 0.62,
      metalness: 0.05,
    });
    const bootBody = new THREE.MeshStandardMaterial({
      color: BOOT_BODY,
      roughness: 0.32,
      metalness: 0.35,
    });
    const bootSole = new THREE.MeshStandardMaterial({
      color: BOOT_SOLE,
      roughness: 0.72,
      metalness: 0.0,
    });
    return { jersey, sleeveTrim, shorts, sock, skin, hair, bootBody, bootSole };
  }, [teamHex, palette]);

  // Selection emissive — apply once per change rather than every frame
  // so we don't need a useFrame here. Coaches reported the prior
  // weight-shift + arm sway + selection-pulse cocktail as too busy;
  // the figure now stands completely still and only changes when
  // selected/deselected.
  // idlePhase + reducedMotion kept on the prop API so PlayerToken3D
  // can pass them in symmetrically with RiggedFootballer; neither
  // affects the rendered pose anymore.
  void idlePhase;
  void reducedMotion;
  const intensity = selected ? 0.32 : 0;
  mats.jersey.emissive.setStyle(teamHex);
  mats.jersey.emissiveIntensity = intensity;
  mats.shorts.emissive.setStyle(palette.shortsHex);
  mats.shorts.emissiveIntensity = intensity * 0.5;

  return (
    <group ref={groupRef}>
      {/* Boots — body + slightly wider sole disc for TV-football realism */}
      <mesh position={[-0.14, 0.05, 0.06]} castShadow material={mats.bootBody}>
        <capsuleGeometry args={[0.07, 0.16, 6, 14]} />
        {/* lay flat, toes forward */}
        <group rotation={[0, 0, Math.PI / 2]} />
      </mesh>
      <mesh position={[-0.14, 0.05, 0.06]} rotation={[Math.PI / 2, 0, 0]} castShadow material={mats.bootBody}>
        <capsuleGeometry args={[0.06, 0.18, 6, 14]} />
      </mesh>
      <mesh position={[0.14, 0.05, 0.06]} rotation={[Math.PI / 2, 0, 0]} castShadow material={mats.bootBody}>
        <capsuleGeometry args={[0.06, 0.18, 6, 14]} />
      </mesh>
      {/* Sole flat caps */}
      <mesh position={[-0.14, 0.015, 0.06]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow material={mats.bootSole}>
        <circleGeometry args={[0.075, 16]} />
      </mesh>
      <mesh position={[0.14, 0.015, 0.06]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow material={mats.bootSole}>
        <circleGeometry args={[0.075, 16]} />
      </mesh>

      {/* Socks — capsule from boot to knee */}
      <mesh position={[-0.14, 0.26, 0]} castShadow material={mats.sock}>
        <capsuleGeometry args={[0.085, 0.18, 6, 16]} />
      </mesh>
      <mesh position={[0.14, 0.26, 0]} castShadow material={mats.sock}>
        <capsuleGeometry args={[0.085, 0.18, 6, 16]} />
      </mesh>

      {/* Lower legs — skin (just below knee, above sock top) */}
      <mesh position={[-0.14, 0.55, 0]} castShadow material={mats.skin}>
        <capsuleGeometry args={[0.072, 0.18, 6, 14]} />
      </mesh>
      <mesh position={[0.14, 0.55, 0]} castShadow material={mats.skin}>
        <capsuleGeometry args={[0.072, 0.18, 6, 14]} />
      </mesh>

      {/* Upper legs — shorts colour, capsule for soft thigh silhouette */}
      <mesh position={[-0.14, 0.84, 0]} castShadow material={mats.shorts}>
        <capsuleGeometry args={[0.09, 0.18, 6, 16]} />
      </mesh>
      <mesh position={[0.14, 0.84, 0]} castShadow material={mats.shorts}>
        <capsuleGeometry args={[0.09, 0.18, 6, 16]} />
      </mesh>

      {/* Shorts hip block — blends thighs into torso. Slightly wider than
          legs but narrower than torso for a sporting taper. */}
      <mesh position={[0, 1.03, 0]} castShadow material={mats.shorts}>
        <capsuleGeometry args={[0.22, 0.12, 6, 18]} />
      </mesh>

      {/* Torso (jersey) — capsule rounds the chest/back; broader than hips
          for the FIFA athletic V-silhouette. */}
      <mesh position={[0, 1.4, 0]} castShadow material={mats.jersey}>
        <capsuleGeometry args={[0.24, 0.36, 8, 20]} />
      </mesh>

      {/* Shoulder pads — small spheres broaden the silhouette so arms
          read as attached to a body, not floating. */}
      <mesh position={[-0.27, 1.62, 0]} castShadow material={mats.jersey}>
        <sphereGeometry args={[0.11, 16, 16]} />
      </mesh>
      <mesh position={[0.27, 1.62, 0]} castShadow material={mats.jersey}>
        <sphereGeometry args={[0.11, 16, 16]} />
      </mesh>

      {/* Sleeve trim ring at the bicep — subtle horizontal accent that
          reads as a kit detail (cuff stripe) at any distance. */}
      <mesh position={[-0.31, 1.5, 0]} rotation={[0, 0, Math.PI / 2]} castShadow material={mats.sleeveTrim}>
        <torusGeometry args={[0.075, 0.012, 8, 18]} />
      </mesh>
      <mesh position={[0.31, 1.5, 0]} rotation={[0, 0, Math.PI / 2]} castShadow material={mats.sleeveTrim}>
        <torusGeometry args={[0.075, 0.012, 8, 18]} />
      </mesh>

      {/* Neck */}
      <mesh position={[0, 1.74, 0]} castShadow material={mats.skin}>
        <capsuleGeometry args={[0.058, 0.06, 4, 14]} />
      </mesh>

      {/* Head — higher-poly sphere catches HDRI cheek/forehead highlights */}
      <mesh position={[0, 1.88, 0]} castShadow material={mats.skin}>
        <sphereGeometry args={[0.13, 24, 24]} />
      </mesh>

      {/* Hair cap — slightly larger sphere with bottom hemisphere clipped,
          sits over the skull. Reads as a short crop at distance. */}
      <mesh position={[0, 1.92, -0.005]} castShadow material={mats.hair}>
        <sphereGeometry args={[0.135, 22, 18, 0, Math.PI * 2, 0, Math.PI / 2.2]} />
      </mesh>

      {/* Arms — pivot at the shoulder so idle sway rotates from there.
          Each arm is a group named "arm-left"/"arm-right" so useFrame
          can pick them up by name. */}
      <group name="arm-left" position={[-0.31, 1.62, 0]}>
        {/* Sleeve (jersey) */}
        <mesh position={[0, -0.13, 0]} castShadow material={mats.jersey}>
          <capsuleGeometry args={[0.073, 0.16, 6, 14]} />
        </mesh>
        {/* Forearm (skin) */}
        <mesh position={[-0.02, -0.42, 0]} castShadow material={mats.skin}>
          <capsuleGeometry args={[0.06, 0.24, 6, 14]} />
        </mesh>
        {/* Hand */}
        <mesh position={[-0.025, -0.62, 0]} castShadow material={mats.skin}>
          <sphereGeometry args={[0.062, 14, 14]} />
        </mesh>
      </group>

      <group name="arm-right" position={[0.31, 1.62, 0]}>
        <mesh position={[0, -0.13, 0]} castShadow material={mats.jersey}>
          <capsuleGeometry args={[0.073, 0.16, 6, 14]} />
        </mesh>
        <mesh position={[0.02, -0.42, 0]} castShadow material={mats.skin}>
          <capsuleGeometry args={[0.06, 0.24, 6, 14]} />
        </mesh>
        <mesh position={[0.025, -0.62, 0]} castShadow material={mats.skin}>
          <sphereGeometry args={[0.062, 14, 14]} />
        </mesh>
      </group>
    </group>
  );
}
